import { ENUM_LABELS, type ImportRowStatus, type ImportRowView } from '#shared/types';
import { Badge, type BadgeTone } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';

const TONES: Record<ImportRowStatus, BadgeTone> = {
  pending: 'neutral',
  ok: 'success',
  warning: 'warning',
  error: 'critical',
  // Grey, not red. A skipped row is the tail of a history sheet with only a
  // year on it — a hundred red rows at the bottom of a preview is how an
  // operator learns to ignore red.
  skipped: 'neutral',
  committed: 'success',
  failed: 'critical',
};

function RowChanges({ row }: { row: ImportRowView }) {
  if (row.changes.length === 0) return <span className="c-muted">—</span>;
  return (
    <ul className="c-import-changes">
      {row.changes.map((change) => (
        <li key={change.field}>
          {change.label}: <s>{change.before ?? 'nothing'}</s> → <b>{change.after ?? 'nothing'}</b>
        </li>
      ))}
    </ul>
  );
}

function RowMessages({ row }: { row: ImportRowView }) {
  if (row.messages.length === 0) return <span className="c-muted">—</span>;
  return (
    <ul className={`c-import-messages`}>
      {row.messages.map((message, index) => (
        <li key={index} className={`c-import-message c-import-message--${message.level}`}>
          {message.message}
        </li>
      ))}
    </ul>
  );
}

/** The staged-row table's columns. Static, so defined once rather than per render. */
export const CHECK_COLUMNS: Column<ImportRowView>[] = [
  {
    key: 'row',
    header: 'Row',
    frozen: true,
    width: '5.5rem',
    render: (row) => (
      <span className="c-sheet__ref">
        {row.row_no}
        {/* 1-based for a human: "table 0" is not a thing anyone can find. */}
        {row.block_index > 0 && <small> · table {row.block_index + 1}</small>}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '9rem',
    render: (row) => (
      <Badge tone={TONES[row.status]}>{ENUM_LABELS.importRowStatus[row.status]}</Badge>
    ),
  },
  {
    key: 'action',
    header: 'Effect',
    width: '7rem',
    render: (row) => (row.action === 'create' ? 'New' : row.action === 'update' ? 'Update' : '—'),
  },
  {
    key: 'summary',
    // Not "Row" — that is the column beside it. This one is the sheet's own
    // cells, which is what the operator scans to find the line they mean.
    header: 'From the sheet',
    render: (row) => (
      <span className="c-import-row__cells">
        {row.cells
          .map((cell) => cell.t)
          .filter(Boolean)
          .slice(0, 4)
          .join(' · ')}
        {row.group_value && <em className="c-import-row__group"> {row.group_value}</em>}
      </span>
    ),
  },
  {
    key: 'changes',
    header: 'What changes',
    render: (row) => <RowChanges row={row} />,
  },
  {
    key: 'messages',
    header: 'Notes',
    render: (row) => <RowMessages row={row} />,
  },
];
